import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { PaletteEntry } from '../model/format.ts';
import { gameData, paletteSprite, type SpriteRef } from '../data/gameData.ts';
import { placementPose } from './catalog.ts';
import { instanceBounds, instanceDrawables, instantiatePrefab } from '../data/prefabInstance.ts';
import { drawScene, drawTile, drawableItem, drawSpriteLocal, TileLayerView } from '../render/renderer.ts';
import { setActiveInput } from './store.ts';

export function Row({ label, children, title, className }: { label: string; children: ReactNode; title?: string; className?: string }) {
  return (
    <label className={`row${className ? ` ${className}` : ''}`} title={title}>
      <span className="row-label">{label}</span>
      <span className="row-value">{children}</span>
    </label>
  );
}

export function Section({ title, children, open = true, right }: { title: string; children: ReactNode; open?: boolean; right?: ReactNode }) {
  const [o, setO] = useState(open);
  return (
    <div className="section">
      <div className="section-head" onClick={() => setO(!o)}>
        <span className={`caret ${o ? 'open' : ''}`}>▸</span>
        <span className="section-title">{title}</span>
        <span className="section-right" onClick={(e) => e.stopPropagation()}>{right}</span>
      </div>
      {o && <div className="section-body">{children}</div>}
    </div>
  );
}

/**
 * While focused, an inspector input applies every edit live and all of them merge into one undo step.
 * The shown text is left alone while typing (the document may round or clamp the value) and re-synced on blur.
 */
function useLiveInput<T>(value: T, format: (v: T) => string) {
  const [text, setText] = useState(format(value));
  const [synced, setSynced] = useState(0);
  const token = useRef<object | null>(null);
  useEffect(() => {
    if (!token.current) setText(format(value));
  }, [value, synced]);
  const focus = () => {
    token.current = {};
    setActiveInput(token.current);
  };
  const blur = () => {
    token.current = null;
    setActiveInput(null);
    setSynced((n) => n + 1);
  };
  useEffect(() => () => {
    if (token.current) setActiveInput(null);
  }, []);
  return { text, setText, focus, blur };
}

const fmtNumber = (v: number) => String(+v.toFixed(4));

export function NumberInput({ value, onChange, step = 1, min, max, width, disabled }: { value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number; width?: number; disabled?: boolean }) {
  const { text, setText, focus, blur } = useLiveInput(value, fmtNumber);
  const latest = useRef(value);
  latest.current = value;
  const parse = (t: string) => (t.trim() === '' ? NaN : Number(t));
  return (
    <input
      className="num"
      style={width ? { width } : undefined}
      type="number"
      inputMode="decimal"
      step={step}
      disabled={disabled}
      value={text}
      onFocus={focus}
      onChange={(e) => {
        setText(e.target.value);
        const v = parse(e.target.value);
        if (Number.isFinite(v) && v >= (min ?? -Infinity) && v <= (max ?? Infinity) && v !== latest.current) onChange(v);
      }}
      onBlur={() => {
        const v = parse(text);
        if (Number.isFinite(v)) {
          const c = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, v));
          if (c !== latest.current) onChange(c);
        }
        blur();
      }}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

export function TextInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const { text, setText, focus, blur } = useLiveInput(value, (v) => v);
  return (
    <input
      className="text"
      value={text}
      placeholder={placeholder}
      onFocus={focus}
      onChange={(e) => {
        setText(e.target.value);
        if (e.target.value !== value) onChange(e.target.value);
      }}
      onBlur={blur}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
    />
  );
}

export function Check({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return <input type="checkbox" className="check" checked={value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />;
}

export function Select<T extends string | number>({ value, options, onChange }: { value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <select
      className="select"
      value={String(value)}
      onChange={(e) => {
        const o = options.find(([k]) => String(k) === e.target.value);
        if (o) onChange(o[0]);
      }}
    >
      {options.map(([k, l]) => (
        <option key={String(k)} value={String(k)}>
          {l}
        </option>
      ))}
    </select>
  );
}

export function ColorInput({ value, onChange }: { value: [number, number, number, number]; onChange: (v: [number, number, number, number]) => void }) {
  const hex = '#' + value.slice(0, 3).map((c) => Math.round(Math.max(0, Math.min(1, c)) * 255).toString(16).padStart(2, '0')).join('');
  return (
    <span className="color-input">
      <input
        type="color"
        value={hex}
        onChange={(e) => {
          const h = e.target.value;
          onChange([parseInt(h.slice(1, 3), 16) / 255, parseInt(h.slice(3, 5), 16) / 255, parseInt(h.slice(5, 7), 16) / 255, value[3]]);
        }}
      />
      <NumberInput value={+value[3].toFixed(3)} step={0.05} min={0} max={1} width={56} onChange={(a) => onChange([value[0], value[1], value[2], a])} />
    </span>
  );
}

// ---------- sprite / prefab thumbnails ----------

export function SpriteThumb({ sprite, size = 32, flags = 0 }: { sprite: SpriteRef | null; size?: number; flags?: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !sprite) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.height = size * dpr;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    g.clearRect(0, 0, c.width, c.height);
    const w = sprite.def.r[2] / sprite.texDef.ppu, h = sprite.def.r[3] / sprite.texDef.ppu;
    const scale = (size * dpr) / Math.max(w, h, 1);
    g.setTransform(scale, 0, 0, -scale, c.width / 2, c.height / 2);
    if (flags) {
      drawTile(g, [{ tex: sprite.tex, sprite: sprite.def.n }], -0.5, -0.5, 0, flags);
    } else {
      g.translate((sprite.def.p[0] - 0.5) * w, (sprite.def.p[1] - 0.5) * h);
      drawSpriteLocal(g, sprite, [1, 1, 1, 1]);
    }
  }, [sprite, size, flags]);
  return <canvas ref={ref} className="thumb" style={{ width: size, height: size }} />;
}

export function PaletteThumb({ entry, size = 32, flags = 0 }: { entry: PaletteEntry | null; size?: number; flags?: number }) {
  return <SpriteThumb sprite={entry ? paletteSprite(entry) : null} size={size} flags={flags} />;
}

const thumbCache = new Map<string, string>();

/** Rendered preview of a prefab (cached as data URL). */
export function PrefabThumb({ guid, size = 48 }: { guid: string; size?: number }) {
  const [url, setUrl] = useState(() => thumbCache.get(`${guid}:${size}`) ?? '');
  useEffect(() => {
    const key = `${guid}:${size}`;
    if (thumbCache.has(key)) {
      setUrl(thumbCache.get(key)!);
      return;
    }
    const pose = placementPose(guid);
    const inst = instantiatePrefab(guid, [], { x: 0, y: 0, rotation: pose.rotation, scale: pose.scale });
    if (!inst) return;
    const c = document.createElement('canvas');
    const dpr = 2;
    c.width = c.height = size * dpr;
    const g = c.getContext('2d')!;
    g.imageSmoothingEnabled = false;
    const b = instanceBounds(inst) ?? [-0.5, -0.5, 0.5, 0.5];
    const w = Math.max(0.5, b[2] - b[0]), h = Math.max(0.5, b[3] - b[1]);
    const scale = (size * dpr * 0.9) / Math.max(w, h);
    g.setTransform(scale, 0, 0, -scale, c.width / 2 - ((b[0] + b[2]) / 2) * scale, c.height / 2 + ((b[1] + b[3]) / 2) * scale);
    const ds = instanceDrawables(inst);
    const views = new Map<string, TileLayerView>();
    drawScene(g, ds.map((d) => drawableItem(d, [b[0] - 1, b[1] - 1, b[2] + 1, b[3] + 1], views)));
    if (!ds.length) {
      g.strokeStyle = '#888';
      g.lineWidth = 2 / scale;
      g.strokeRect(b[0], b[1], w, h);
    }
    const u = c.toDataURL();
    thumbCache.set(key, u);
    setUrl(u);
  }, [guid, size]);
  return url ? <img className="thumb" src={url} width={size} height={size} alt="" /> : <span className="thumb" style={{ width: size, height: size }} />;
}

export function prefabName(guid: string | null | undefined): string {
  if (!guid) return 'Custom';
  return gameData().prefabs[guid]?.name ?? guid.slice(0, 8);
}

export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  const p = ICONS[name] ?? ICONS.dot;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {p}
    </svg>
  );
}

const ICONS: Record<string, ReactNode> = {
  dot: <circle cx="12" cy="12" r="3" />,
  select: <path d="M5 3l14 8-6 2-2 6z" />,
  pan: <path d="M12 2v20M2 12h20M12 2l-3 3M12 2l3 3M12 22l-3-3M12 22l3-3M2 12l3-3M2 12l3 3M22 12l-3-3M22 12l-3 3" />,
  polygon: <path d="M4 18l4-10h6l6 10z M4 18h16" />,
  topoly: <path d="M3 3h6v6H3zM11 3h6v6h-6zM3 11h6v6H3zM13 13l8 3-3 7h-7z" />,
  brush: <path d="M3 21c3 0 5-2 5-4a3 3 0 10-3-3c0 2-2 3-2 7zM9 13L20 2" />,
  erase: <path d="M7 21h10M4 15l8-8 6 6-6 6H8z" />,
  rect: <rect x="4" y="5" width="16" height="14" rx="1" />,
  grass: <path d="M3 20h18M5 20c0-4 1-7 3-9M9 20c0-5 1-9 4-12M14 20c0-3 1-6 3-8M18 20c0-2 .5-4 2-5" />,
  spikes: <path d="M3 20h18M4 20l3.5-10 3.5 10 3.5-10L18 20" />,
  foliage: <path d="M5 20C5 11 10 5 20 4c-1 10-7 15-15 16zM5 20l8-8" />,
  flowers: <path d="M12 21v-8M12 13c-3 0-4 3-6 3M12 7a2 2 0 110 4 2 2 0 010-4zM12 7V5M12 11v2M10 9H8M14 9h2M10.6 7.6L9.2 6.2M13.4 7.6l1.4-1.4M10.6 10.4L9.2 11.8M13.4 10.4l1.4 1.4" />,
  vines: <path d="M4 3h16M8 3c0 4 3 5 3 9s-3 5-3 9M16 3c0 3-2 4-2 7M8 8c-2 0-3 1-3 2 2 0 3-1 3-2zM11 14c2 0 3 1 3 2-2 0-3-1-3-2zM14 8c2 0 3 1 3 2-2 0-3-1-3-2z" />,
  trees: <path d="M12 21v-6M12 3a6 6 0 016 6 4 4 0 01-2 6H8a4 4 0 01-2-6 6 6 0 016-6zM12 15l-3-3M12 17l3-3M8 21h8" />,
  rocks: <path d="M3 19l3-6 4-2 3 1 3-3 3 2 2 8zM10 11l1 8" />,
  props: <path d="M4 10h16v10H4zM4 10l2-5h12l2 5M10 14h4" />,
  details: <path d="M3 8h18v12H3zM3 8c3 2 5-1 8 1s5-1 10 0M8 14a2 2 0 104 0 2 2 0 00-4 0M15 16h2" />,
  eyedropper: <path d="M14 4l6 6-9 9H5v-6zM12 6l6 6" />,
  fix: <path d="M3 3h7v7H3zM3 14h7v7H3zM14 3h7v7h-7zM14 17l2 2 4-5" />,
  object: <path d="M12 2l9 5v10l-9 5-9-5V7zM3 7l9 5 9-5M12 12v10" />,
  waypoint: <path d="M5 19c4 0 3-14 7-14s3 14 7 14M5 19h.01M19 19h.01" />,
  link: <path d="M10 14a4 4 0 005.6 0l3-3a4 4 0 00-5.6-5.6l-1 1M14 10a4 4 0 00-5.6 0l-3 3a4 4 0 005.6 5.6l1-1" />,
  detach: <path d="M3 3h8v8H3zM13 13h8v8h-8zM11 7h4v4M13 11l4-4" />,
  undo: <path d="M9 14L4 9l5-5M4 9h11a5 5 0 010 10h-3" />,
  redo: <path d="M15 14l5-5-5-5M20 9H9a5 5 0 000 10h3" />,
  play: <path d="M7 4l13 8-13 8z" />,
  stop: <rect x="6" y="6" width="12" height="12" rx="1.5" />,
  sound: <path d="M4 9h4l5-4v14l-5-4H4zM16.5 9a4 4 0 010 6M19 6.5a7.5 7.5 0 010 11" />,
  playhere: <path d="M12 2v3M12 19v3M2 12h3M19 12h3M12 5a7 7 0 110 14 7 7 0 010-14zM10 9l5 3-5 3z" />,
  target: <path d="M12 2v4M12 18v4M2 12h4M18 12h4M12 6a6 6 0 110 12 6 6 0 010-12z" />,
  save: <path d="M5 3h11l3 3v15H5zM8 3v5h7V3M8 21v-7h8v7" />,
  folder: <path d="M3 6h6l2 2h10v11H3z" />,
  grid: <path d="M3 3h18v18H3zM9 3v18M15 3v18M3 9h18M3 15h18" />,
  cases: <path d="M3 3h8v8H3zM3 11l8-8M13 3h8v8h-8zM13 11l8-4M3 13h8v8H3zM3 21l8-4M13 13h8v8h-8zM13 21l4-8" />,
  tileset: <path d="M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z" />,
  menu: <path d="M3 6h18M3 12h18M3 18h18" />,
  close: <path d="M5 5l14 14M19 5L5 19" />,
  eye: <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 9a3 3 0 110 6 3 3 0 010-6z" />,
  eyeoff: <path d="M3 3l18 18M10.6 10.6a3 3 0 004.2 4.2M9.9 5.1A10 10 0 0112 5c6 0 10 7 10 7a17 17 0 01-3.2 3.9M6.6 6.6C3.8 8.4 2 12 2 12s4 7 10 7a10 10 0 005.4-1.6" />,
  lock: <path d="M6 11h12v10H6zM8 11V7a4 4 0 018 0v4" />,
  unlock: <path d="M6 11h12v10H6zM8 11V7a4 4 0 017.5-2" />,
  plus: <path d="M12 5v14M5 12h14" />,
  trash: <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />,
  up: <path d="M12 19V5M5 12l7-7 7 7" />,
  collapse: <path d="M9 6l6 6-6 6M19 4v16" />,
  expand: <path d="M15 6l-6 6 6 6M5 4v16" />,
  down: <path d="M12 5v14M19 12l-7 7-7-7" />,
  enter: <path d="M9 10l-5 5 5 5M20 4v7a4 4 0 01-4 4H4" />,
  exit: <path d="M10 17l5-5-5-5M15 12H3M21 3v18" />,
  download: <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />,
  upload: <path d="M12 21V9M7 14l5-5 5 5M5 3h14" />,
  help: <path d="M9.1 9a3 3 0 015.8 1c0 2-3 3-3 3M12 17h.01M12 2a10 10 0 110 20 10 10 0 010-20z" />,
  flipx: <path d="M12 3v18M8 7L3 12l5 5zM16 7l5 5-5 5z" />,
  flipy: <path d="M3 12h18M7 8l5-5 5 5zM7 16l5 5 5-5z" />,
  rotate: <path d="M20 11a8 8 0 10-2.3 5.7M20 4v7h-7" />,
  refresh: <path d="M20 12a8 8 0 01-14.3 4.9M4 12a8 8 0 0114.3-4.9M18.3 3v4.1h-4.1M5.7 21v-4.1h4.1" />,
  hole: <path d="M4 4h16v16H4zM9 9h6v6H9z" />,
  minus: <path d="M5 12h14" />,
  settings: <path d="M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />,
  check: <path d="M5 12l5 5L20 7" />,
  history: <path d="M3 12a9 9 0 103-6.7M3 4v5h5M12 7v5l3 2" />,
  star: <path d="M12 3l2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z" />,
  copy: <path d="M8 8h12v12H8zM4 16V4h12" />,
  panelopen: <path d="M3 4h18v16H3zM9 4v16M13 9l3 3-3 3" />,
  panelclose: <path d="M3 4h18v16H3zM9 4v16M16 9l-3 3 3 3" />,
  people: <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a3 3 0 100-6 3 3 0 000 6zM22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />,
  layers: <path d="M12 2l10 5-10 5L2 7zM2 12l10 5 10-5M2 17l10 5 10-5" />,
  pencil: <path d="M4 20l1-5L16 4l4 4L9 19zM14 6l4 4" />,
  line: <path d="M5 19L19 5" />,
  bucket: <path d="M5 11l7-7 7 7-7 7zM5 11h14M20 15c0 2 1 3 1 4a1 1 0 01-2 0c0-1 1-2 1-4z" />,
  marquee: <path d="M4 4h3M10 4h4M17 4h3v3M20 10v4M20 17v3h-3M14 20h-4M7 20H4v-3M4 14v-4M4 7V4" />,
  lasso: <path d="M12 4c5 0 8 2.5 8 5.5S16.5 15 12 15 4 12.5 4 9.5 7 4 12 4zM7 14c-1 1.5-1 3 1 4s1 3-1 3" />,
  pixel: <path d="M4 4h7v7H4zM13 13h7v7h-7zM13 4h7v7h-7z" />,
  pin: <path d="M9 3h6l-1 6 3 3v2H7v-2l3-3zM12 14v7" />,
  bulb: <path d="M9 21h6M10 17h4M12 3a6 6 0 00-4 10c.6.6 1 1.5 1 2.4V16h6v-.6c0-.9.4-1.8 1-2.4A6 6 0 0012 3z" />,
  bug: <path d="M12 7a4 4 0 014 4v4a4 4 0 01-8 0v-4a4 4 0 014-4zM8 9L4 7M16 9l4-2M8 13H3M21 13h-5M8 17l-4 2M16 17l4 2M12 7V3" />,
  cut: <path d="M6 9a3 3 0 100-6 3 3 0 000 6zM6 21a3 3 0 100-6 3 3 0 000 6zM8.5 7.5L20 19M8.5 16.5L20 5" />,
  paste: <path d="M9 4h6v3H9zM7 5H5v16h14V5h-2" />,
  mirror: <path d="M12 3v18M4 7l5 5-5 5zM20 7l-5 5 5 5z" />,
};
